import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, History, Eye, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePreferences } from '@/context/PreferencesContext';

const AIConsentModal = ({ open, onAccept, onDecline }) => {
    const [accepted, setAccepted] = useState(false);
    const { theme } = usePreferences();
    const isDark = theme !== 'light';

    const handleConsentAccept = () => {
        onAccept();
    };

    return (
        <Dialog open={open} onOpenChange={(val) => { if (!val) onDecline(); }}>
            <DialogContent className={cn(
                "sm:max-w-[460px] border border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] text-foreground shadow-2xl p-0 overflow-hidden rounded-3xl flex flex-col",
                "max-sm:fixed max-sm:inset-x-4 max-sm:top-[calc(3.5rem+env(safe-area-inset-top,0px)+0.75rem)] max-sm:bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-h-none sm:max-h-[90vh]"
            )}>
                {/* Header */}
                <DialogHeader className="px-6 py-4 max-sm:py-2 text-center border-b border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] flex flex-col items-center justify-center relative">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center mb-2 max-sm:mb-0 shadow-md bg-gradient-to-br from-[hsl(var(--green-bright))]/15 to-[hsl(var(--teal-vivid))]/15 text-[hsl(var(--teal-vivid))] border border-[hsl(var(--teal-vivid))]/20">
                        <ShieldCheck className="w-7 h-7" />
                    </div>
                    <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                        AI Data Consent
                    </DialogTitle>
                    <p className="text-xs mt-1 text-muted-foreground">
                        Please review before using Budlee AI
                    </p>
                </DialogHeader>

                {/* Body */}
                <div className="p-5 space-y-4 overflow-y-auto flex-1 scrollbar-thin">
                    <p className="text-xs font-medium leading-relaxed text-foreground">
                        To provide you with the best educational assistance, Budlee AI uses advanced artificial intelligence.
                    </p>

                    <div className="space-y-2">
                        <div className="flex gap-2.5 py-2 px-3 rounded-xl border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--sidebar-hover))] transition-colors">
                            <History className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--teal-vivid))]" />
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                <span className="font-bold text-[12px] mr-1.5 text-foreground">Chat History:</span>
                                Conversations are saved to review later and track learning progress.
                            </p>
                        </div>

                        <div className="flex gap-2.5 py-2 px-3 rounded-xl border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--sidebar-hover))] transition-colors">
                            <Eye className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--teal-vivid))]" />
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                <span className="font-bold text-[12px] mr-1.5 text-foreground">Privacy &amp; Safety:</span>
                                Data is used to improve models. Avoid sharing passwords or sensitive personal details.
                            </p>
                        </div>

                        <div className="flex gap-2.5 py-2 px-3 rounded-xl border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--sidebar-hover))] transition-colors">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500 dark:text-amber-400" />
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                <span className="font-bold text-[12px] mr-1.5 text-foreground">Accuracy Warning:</span>
                                AI can occasionally make mistakes. Always verify important facts with your textbook.
                            </p>
                        </div>
                    </div>

                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        By clicking "Agreed and Continue", you consent to our processing of your chat data as described in our{' '}
                        <a
                            href="https://www.budlee.ai/privacy"
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold hover:underline transition-all text-[hsl(var(--teal-vivid))]"
                        >
                            Privacy Policy
                        </a>.
                    </p>

                    <div
                        className={cn(
                            "flex items-center space-x-3 p-3 rounded-xl border transition-all cursor-pointer select-none",
                            accepted
                                ? "bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]/30"
                                : "bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] hover:border-[hsl(var(--primary))]/30"
                        )}
                        onClick={() => setAccepted(prev => !prev)}
                    >
                        <Checkbox
                            id="consent-check"
                            checked={accepted}
                            className="pointer-events-none"
                        />
                        <span className="text-xs font-medium leading-none pointer-events-none text-foreground">
                            I understand and agree to the AI data usage terms.
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <DialogFooter className="p-4 border-t border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] flex flex-col sm:flex-row gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onDecline}
                        className="w-full sm:w-auto border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))]"
                    >
                        Not Now
                    </Button>
                    <Button
                        type="button"
                        disabled={!accepted}
                        onClick={handleConsentAccept}
                        className="w-full sm:w-auto ml-auto bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-95 text-white font-semibold px-8 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                    >
                        Agreed and Continue
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AIConsentModal;
